import { css } from '@emotion/react';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Button, ListGroup, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import { numberWithCommas, getTeamIdFromURL } from '../../../../../_helpers';
import VisitComment from '../VisitComment';
import { getVisitUnitsAndAmount } from '../VisitUtils';
import { VisitChangeLabelAction, VisitRedirectToChannel, VisitStatus, VisitView } from './VisitRowActionButtons';
import { changeVisitLabel } from '@/_redux/actions/ActionEVV';

const isVerifiedVisit = (v) =>
    v?.status === 'verified' || v?.extraInfo?.label === 'verified' || v?.label === 'verified';

function CombinedVisitModal({
    show,
    group,
    handleClose,
    onViewVisit,
    onGroupUpdate,
    labels = {},
    teamDetails,
    teamId,
    changeVisitLabel,
    evvVisits,
}) {
    const [visits, setVisits] = useState(group?.visits || []);
    const [labelLoading, setLabelLoading] = useState({});

    const visibleVisits = visits.filter(isVerifiedVisit);

    useEffect(() => {
        setVisits(group?.visits || []);
    }, [group]);

    useEffect(() => {
        if (!evvVisits?.length) return;
        setVisits((prev) => {
            const merged = prev.map((v) => {
                const match = evvVisits.find((sv) => (sv?.id || sv?._id) === (v?.id || v?._id));
                return match ? { ...v, ...match, extraInfo: { ...v.extraInfo, ...match.extraInfo } } : v;
            });
            return merged.filter(isVerifiedVisit);
        });
    }, [evvVisits]);

    if (!group) return null;

    const summary = group?.summary || {};
    const team = teamId || getTeamIdFromURL();

    // Remove a visit from the combined group
    const handleRemove = (visitId) => {
        const updated = visits.filter((v) => (v?._id || v?.id) !== visitId);
        setVisits(updated);
        if (onGroupUpdate) onGroupUpdate(updated);
    };

    // Change label for an individual visit
    const handleLabelChange = async (visit, newLabel) => {
        const visitId = visit?._id || visit?.id;
        setLabelLoading((prev) => ({ ...prev, [visitId]: true }));
        try {
            const response = await changeVisitLabel({
                visitId,
                label: newLabel,
                team,
            });
            setVisits((prev) => {
                const resolvedLabel = response?.label || newLabel;
                const updatedVisits = prev.map((v) =>
                    (v?._id || v?.id) === visitId
                        ? { ...v, label: resolvedLabel, extraInfo: { ...v.extraInfo, label: resolvedLabel } }
                        : v
                );
                if (onGroupUpdate) onGroupUpdate(updatedVisits);
                return updatedVisits;
            });
            toast.success('Label updated');
        } catch (err) {
            toast.error('Failed to update label');
        } finally {
            setLabelLoading((prev) => ({ ...prev, [visitId]: false }));
        }
    };

    return (
        <Modal show={Boolean(show)} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Combined Visit</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ overflowY: 'auto', maxHeight: '75vh' }}>
                <ListGroup
                    css={css`
                        .list-group-item {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                        }
                    `}
                >
                    <ListGroup.Item>
                        <b>Start Date:</b>
                        <span>{summary?.startDate}</span>
                    </ListGroup.Item>
                    <ListGroup.Item>
                        <b>Service Code:</b>
                        <span>
                            {summary?.serviceCode || 'Not provided'}
                            {summary?.extraInfo?.Modifier1 ? summary?.extraInfo?.Modifier1 : ' - '}{' '}
                            {summary?.extraInfo?.Modifier2 ? summary?.extraInfo?.Modifier2 : ' - '}{' '}
                            {summary?.extraInfo?.Modifier3 ? summary?.extraInfo?.Modifier3 : ' - '}{' '}
                            {summary?.extraInfo?.Modifier4 ? summary?.extraInfo?.Modifier4 : ' - '}{' '}
                        </span>
                    </ListGroup.Item>
                    <ListGroup.Item>
                        <b>Total Units:</b>
                        <span>{summary?.totalUnits || 0}</span>
                    </ListGroup.Item>
                    <ListGroup.Item>
                        <b>Total Billed Amount:</b>
                        <span>${numberWithCommas((summary?.totalAmount || 0).toFixed(2))}</span>
                    </ListGroup.Item>
                    <ListGroup.Item>
                        <b>Visits in group:</b>
                        <span>{visibleVisits.length}</span>
                    </ListGroup.Item>
                </ListGroup>

                <div
                    css={css`
                        margin-top: 20px;
                        font-weight: 600;
                        color: #333;
                    `}
                >
                    Visits in this combined row:
                </div>

                <ListGroup
                    css={css`
                        margin-top: 10px;
                    `}
                >
                    {visibleVisits.map((visit, visitIndex) => {
                        const { billUnit, serviceAmount, staffRate, serviceRate, staffAmount } =
                            getVisitUnitsAndAmount(visit);
                        const visitId = visit?._id || visit?.id;
                        const visitLabel = (visit?.extraInfo || {})?.label || visit?.label;

                        return (
                            <ListGroup.Item
                                key={visitId}
                                css={css`
                                    flex-direction: column;
                                    align-items: flex-start !important;
                                    gap: 10px;
                                    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
                                    margin-bottom: 20px;
                                `}
                            >
                                {/* Visit info row */}
                                <div
                                    css={css`
                                        display: flex;
                                        justify-content: space-between;
                                        width: 100%;
                                    `}
                                >
                                    <div>
                                        <div>
                                            [{visitIndex + 1}]{' '}
                                            <span
                                                className="badge w-50 ml-2"
                                                css={css`
                                                    background: ${labels[visitLabel]};
                                                `}
                                            >
                                                {visitLabel}
                                            </span>
                                        </div>
                                        <div>
                                            <b>Client:</b>{' '}
                                            {visit?.client
                                                ? `${visit?.client?.firstName} ${visit?.client?.lastName}`
                                                : 'Deleted Client'}
                                        </div>
                                        <div>
                                            <b>Employee:</b>{' '}
                                            {visit?.employee && (visit?.employee?.user || {})?.name
                                                ? visit?.employee?.user?.name
                                                : 'Deleted Employee'}
                                        </div>
                                        <div>
                                            <b>Start:</b> {dayjs(visit?.ScheduleStartTime).format('MM-DD-YYYY, h:mm A')}
                                        </div>
                                        <div>
                                            <b>End:</b> {dayjs(visit?.ScheduleEndTime).format('MM-DD-YYYY, h:mm A')}
                                        </div>
                                        <div>
                                            <b>Units:</b> {billUnit}
                                        </div>
                                        <div>
                                            <b>Staff Rate (Per Hour):</b>
                                            <span className="ml-2">$ {staffRate}</span>
                                        </div>
                                        <div>
                                            <b>Service Rate (Per Unit):</b>
                                            <span className="ml-2">$ {serviceRate}</span>
                                        </div>
                                        <hr />
                                        <div>
                                            <b>Staff Amount:</b>
                                            <span className="ml-2">$ {staffAmount}</span>
                                        </div>
                                        <div>
                                            <b>Service Amount:</b>
                                            <span className="ml-2">$ {serviceAmount}</span>
                                        </div>
                                        {/* <VisitStatus visitData={visit} /> */}
                                    </div>
                                    <div
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                            gap: 6px;
                                            align-items: flex-end;
                                        `}
                                    >
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleRemove(visitId)}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                </div>

                                <div
                                    css={css`
                                        width: 100%;
                                        display: flex;
                                        align-items: center;
                                        gap: 8px;
                                        margin-top: 6px;
                                        flex-wrap: wrap;
                                    `}
                                >
                                    <VisitView visit={visit} visitData={visit} handleShow={onViewVisit} />
                                    <VisitChangeLabelAction
                                        isLabelChanging={Boolean(labelLoading?.[visitId])}
                                        onChangeLabel={(newLabel) => handleLabelChange(visit, newLabel)}
                                        labels={labels}
                                        teamDetails={teamDetails}
                                        teamId={team}
                                    />
                                    <VisitRedirectToChannel visit={visit} />
                                </div>
                                <div
                                    css={css`
                                        width: 100%;
                                        margin-top: 6px;
                                    `}
                                >
                                    <VisitComment visit={visit} />
                                </div>
                            </ListGroup.Item>
                        );
                    })}
                </ListGroup>

                {visibleVisits.length === 0 && (
                    <div
                        css={css`
                            text-align: center;
                            margin-top: 20px;
                            color: #888;
                        `}
                    >
                        All visits removed from this group.
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

const mapStateToProps = (state) => ({
    evvVisits: state?.evv?.visits?.visits || [],
});

export default connect(mapStateToProps, { changeVisitLabel })(CombinedVisitModal);
